import { HighlightOffOutlined } from '@mui/icons-material';
import {
  Dialog,
  DialogTitle,
  IconButton,
  DialogContent,
  Grid,
  TextField,
  Select,
  MenuItem,
  Container,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import {
  useCreateMachineGroupMutation,
  useListCustomersQuery,
  useUpdateMachineGroupMutation,
} from 'src/backend';
import DataField from 'src/components/DataField/DataField';
import LoadingButton from 'src/components/LoadingButton';
import { MachineGroup } from './MachineGroups';

export enum MachineType {
  SmartBagger,
  EasyPack,
}

export type Customer = {
  id: string;
  name: string;
};

export type UpdateMachineGroupRequest = {
  name: string;
  machineType: string;
};

export type CreateMachineGroupRequest = UpdateMachineGroupRequest & {
  customerId: string;
};

type Params = {
  isOpen: boolean;
  group: MachineGroup | null;
  closeForm: (refetchRequired: boolean) => void;
};

function CreateEditMachineGroupForm({ isOpen, group, closeForm }: Params) {
  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [id, setId] = useState(group?.id);
  const [isEdit, setIsEdit] = useState(group?.id !== null);
  const [nameOnlyEdit, setNameOnlyEdit] = useState(
    group != null && group?.machinesCount > 0,
  );
  const [groupName, setGroupName] = useState(group?.name ?? '');
  const [selectedMachineType, setMachineType] = useState(
    group?.machineType ?? '',
  );
  const [selectedCustomerId, setCustomerId] = useState(group?.customerId ?? '');

  useMemo(() => {
    setId(group?.id);
    setIsEdit(group?.id !== null && group?.id !== undefined);
    setNameOnlyEdit(group != null && group?.machinesCount > 0);
    setGroupName(group?.name ?? '');
    setMachineType(group?.machineType ?? '');
    setCustomerId(group?.customerId ?? '');
  }, [
    group,
    setId,
    setIsEdit,
    setNameOnlyEdit,
    setGroupName,
    setMachineType,
    setCustomerId,
  ]);

  const createMachineGroup = useCreateMachineGroupMutation();
  const updateMachineGroup = useUpdateMachineGroupMutation();
  const { data, refetch, isLoading } = useListCustomersQuery(false);

  useEffect(() => {
    refetch();
  }, [refetch]);
  useEffect(() => {
    if (
      groupName &&
      groupName.length > 0 &&
      selectedMachineType &&
      selectedMachineType.length > 0 &&
      selectedCustomerId &&
      selectedCustomerId.length > 0
    )
      setSubmitDisabled(false);
    else {
      setSubmitDisabled(true);
    }
  }, [groupName, selectedMachineType, selectedCustomerId]);

  const handleNameChange = (value: string) => {
    if (!value || value.length === 0) {
      setSubmitDisabled(true);
    }
    setGroupName(value);
  };

  const handleCustomerChange = (value: string) => {
    if (!value || value.length === 0) {
      setSubmitDisabled(true);
      return;
    }
    setCustomerId(value);
  };

  const handleMachineTypeChange = (value: string) => {
    if (!value || value.length === 0) {
      setSubmitDisabled(true);
      return;
    }
    setMachineType(value);
  };

  const updateGroup = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    const request: UpdateMachineGroupRequest = {
      name: groupName,
      machineType: selectedMachineType,
    };
    updateMachineGroup[0]({ id, updateMachineGroupRequest: request })
      .unwrap()
      .then(() => {
        closeForm(true);
      })
      .catch(() => closeForm(false))
      .finally(() => {
        setGroupName('');
        setMachineType('');
        setCustomerId('');
        setSubmitDisabled(true);
      });
  };

  const createGroup = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    const request: CreateMachineGroupRequest = {
      name: groupName,
      machineType: selectedMachineType,
      customerId: selectedCustomerId,
    };
    createMachineGroup[0](request)
      .unwrap()
      .then(() => {
        closeForm(true);
      })
      .catch(() => closeForm(false))
      .finally(() => {
        setGroupName('');
        setMachineType('');
        setCustomerId('');
        setSubmitDisabled(true);
      });
  };

  const handleSubmit = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    if (isEdit) {
      updateGroup(e);
    } else {
      createGroup(e);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={() => closeForm(false)}
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle>
        Machine group
        <IconButton onClick={() => closeForm(false)} style={{ float: 'right' }}>
          <HighlightOffOutlined />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <Grid item columnSpacing={2}>
            <Grid item xs="auto" rowGap={1}>
              <DataField
                label="Group name *"
                labelPadding="10px 0 0 0"
                value={
                  <TextField
                    variant="outlined"
                    inputProps={{
                      style: { width: '220px' },
                    }}
                    value={groupName}
                    placeholder="Group name"
                    name="groupName"
                    required
                    onChange={(e) => handleNameChange(e.target.value)}
                  />
                }
              />
              <DataField
                label="Machine type *"
                labelPadding="10px 0 0 0"
                value={
                  <Select
                    sx={{
                      marginBottom: '10px',
                      width: '250px',
                      height: '40px',
                    }}
                    disabled={nameOnlyEdit}
                    name="machineType"
                    type="string"
                    value={selectedMachineType ?? 'SmartBagger'}
                    onChange={(e) => handleMachineTypeChange(e.target.value)}
                  >
                    <MenuItem value={MachineType[MachineType.SmartBagger]}>
                      SmartBagger
                    </MenuItem>
                    <MenuItem value={MachineType[MachineType.EasyPack]}>
                      EasyPack
                    </MenuItem>
                  </Select>
                }
              />
              <DataField
                label="Customer *"
                labelPadding="10px 0 0 0"
                value={
                  <Select
                    sx={{
                      marginBottom: '10px',
                      width: '250px',
                      height: '40px',
                    }}
                    disabled={isEdit}
                    name="customer"
                    type="string"
                    value={selectedCustomerId}
                    onChange={(e) => handleCustomerChange(e.target.value)}
                  >
                    {data?.data.map((customer) => (
                      <MenuItem key={customer.id} value={customer.id}>
                        {customer.name}
                      </MenuItem>
                    ))}
                  </Select>
                }
              />
              <Container style={{ marginTop: '5em', alignItems: 'end' }}>
                <LoadingButton
                  loading={
                    isLoading ||
                    createMachineGroup[1].isLoading ||
                    updateMachineGroup[1].isLoading
                  }
                  variant="outlined"
                  type="submit"
                  disabled={submitDisabled}
                >
                  Submit
                </LoadingButton>
              </Container>
            </Grid>
          </Grid>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default CreateEditMachineGroupForm;
